// Online play: a shared room, one game state, everybody watching it.
//
// The house shape (thunderstone → camel-up): client-authoritative
// full-snapshot sync over Firebase RTDB, every move through the same
// engine.dispatch() as a local game, a seq transaction so two phones can't
// land the same move twice.
//
// What is different here, because this is a co-op game with two kinds of
// screen:
//
//  - Seats are ROLES, not chairs. The lobby's `roles` map says which person
//    holds Culture, Trade, Science and Military; one person may hold several
//    (a solo player holds all four). Unclaimed roles fall to the host.
//  - A TABLE is a client that joins the room to SHOW it - the TV in the
//    living room, or two TVs in two living rooms - and never takes a role.
//    It binds to the same state as the phones and simply renders more of it.
//  - There are no bots. A co-op game with a missing player waits for them.

import {
  ref as dbRef, onValue, onDisconnect, push, set, get, update, remove, runTransaction, goOffline, goOnline,
} from 'firebase/database';
import { db } from '../firebase';
import { generateRoomCode, normalizeRoomCode } from '../utils/roomCodes';
import { createGameState, botFor } from '../engine/game';
import { ROLES, ROLE_INFO, MAX_PLAYERS } from '../engine/constants';

export const DEAD_AFTER_MS = {
  lobby: 60 * 60 * 1000,
  // A campaign is played over ~10 sittings and lasts until it is deleted
  // (DESIGN.md "The Year"). It used to expire after a week.
  'in-progress': Infinity,
  finished: 30 * 24 * 60 * 60 * 1000,
};

export const lastActiveOf = (meta) => {
  if (Number.isFinite(meta?.lastActiveAt)) return meta.lastActiveAt;
  const parsed = Date.parse(meta?.createdAt || '');
  return Number.isFinite(parsed) ? parsed : 0;
};

export const isDeadRoom = (meta, presence, now = Date.now()) => {
  if (!meta) return false;
  if (Object.keys(presence || {}).length > 0) return false;
  const age = now - lastActiveOf(meta);
  const limit = DEAD_AFTER_MS[meta.status] ?? DEAD_AFTER_MS.lobby;
  return age > limit;
};

export const GAMES = 'commonwealthGames';
export const CODES = 'commonwealthGameCodes';
export const USER_GAMES = 'commonwealthUserGames';
// FRIENDS' GAMES (Matt, 2026-10-16: "I should be able to see games that my
// friends have set up on the home screen without having to know the code").
// Nobody may list every game, so a lobby is also written here - a few words,
// no state - from creation until it starts or is deleted. The landing shows
// the ones hosted by, or holding, somebody you have played with before.
export const OPEN = 'commonwealthOpenRooms';
export const OPEN_FOR_MS = 24 * 60 * 60 * 1000;

// The state is stored as a JSON STRING, not as a tree - RTDB drops empty
// arrays and objects, and this state is full of them (an empty discard, an
// empty tech list). Stringifying keeps the round trip byte-identical.
/** The open-rooms entry for a lobby: who is in it, by uid, and how to join it. */
export const openEntry = (meta) => {
  const host = playersIn(meta).find((p) => p.uid === meta.hostUid);
  return {
    hostUid: meta.hostUid,
    hostName: host?.displayName || null,
    name: meta.name || meta.roomCode,
    roomCode: meta.roomCode,
    openedAt: Date.parse(meta.createdAt || '') || Date.now(),
    uids: playersIn(meta).map((p) => p.uid),
  };
};

/** Everybody you have shared a room with: the uids in your games' slots, bots and you aside. */
export const friendsOf = (metas, me) => {
  const out = new Set();
  for (const meta of metas || []) {
    for (const s of meta?.playerSlots || []) {
      if (s?.uid && s.uid !== me && !s.bot) out.add(s.uid);
    }
    if (meta?.hostUid && meta.hostUid !== me) out.add(meta.hostUid);
  }
  return [...out];
};

/**
 * The open rooms worth showing you: a friend hosts it or sits in it, you do
 * not host it yourself, it is under a day old, and it has room for you (or
 * already holds you). Newest first.
 */
export const friendsGames = (open, friends, me, now = Date.now()) => {
  const known = new Set(friends || []);
  return Object.entries(open || {})
    .map(([gameId, r]) => ({ gameId, ...r, uids: r?.uids || [] }))
    .filter((r) => r.roomCode && r.hostUid !== me)
    .filter((r) => now - (r.openedAt || 0) < OPEN_FOR_MS)
    .filter((r) => known.has(r.hostUid) || r.uids.some((u) => known.has(u)))
    .filter((r) => r.uids.includes(me) || r.uids.length < MAX_PLAYERS)
    .map((r) => ({ ...r, joined: r.uids.includes(me) }))
    .sort((a, b) => b.openedAt - a.openedAt);
};

/** Is this open-rooms entry past keeping? */
export const staleOpen = (r, now = Date.now()) => !r?.roomCode || now - (r.openedAt || 0) >= OPEN_FOR_MS;

const closeOpen = (database, gameId) => remove(dbRef(database, `${OPEN}/${gameId}`)).catch(() => {});

const encode = (state) => JSON.stringify(state);
const decode = (raw) => (raw ? JSON.parse(raw) : null);

export const seqOf = (state) => state?.meta?.seq ?? 0;

/** The roles map with every unclaimed role handed to the host. */
/**
 * The people in the room. The TV may hold a slot - the database rules
 * require `playerSlots` to exist and Firebase drops an empty array, so a
 * table-hosted room seats the table itself and flags it - but it is not a
 * player and must never be counted as one.
 */
export const playersIn = (meta) => [...(meta?.playerSlots || [])]
  .filter((s) => !s.table)
  .sort((a, b) => a.seatIndex - b.seatIndex);

/**
 * Does this person still need a PLAYER slot? The table's own slot does not
 * count as one, and it usually carries the same uid as the phone that opened
 * the room: one person, one TV, one phone, one account. Without this, the
 * host's phone looked like it had already joined a room where the only slot
 * was the television, so the lobby said "nobody has joined yet" while showing
 * "someone" against every role.
 */
export const needsSlot = (meta, uid) => !(meta?.playerSlots || [])
  .some((s) => s.uid === uid && !s.table);

// Who holds a role nobody claimed. Normally the host - but the host can be
// the TV, and a role held by the TV is a role nobody can play. When the host
// took no seat, the leftovers go to the first person who sat down instead.
const fallbackHolder = (meta) => {
  const seats = playersIn(meta);
  if (seats.some((s) => s.uid === meta?.hostUid)) return meta.hostUid;
  return seats[0]?.uid || null;
};

export const settledRoles = (meta) => ROLES.reduce((acc, role) => ({
  ...acc, [role]: meta?.roles?.[role] || fallbackHolder(meta) || null,
}), {});

// `asTable` opens the room FROM the big screen: it hosts, shows the code and
// the QR, and starts the game - but takes no seat and holds no role, so the
// phones that scan in get everything. See settledRoles above.
export const createRoom = async ({ user, asTable = false, name = '' }) => {
  if (!db) throw new Error('Online play needs Firebase configured (see .env.example).');
  if (!user) throw new Error('Sign in first.');

  const gameRef = push(dbRef(db, GAMES));
  const gameId = gameRef.key;

  let code;
  let attempts = 0;
  do {
    code = generateRoomCode(attempts);
    attempts += 1;
    // eslint-disable-next-line no-await-in-loop
  } while (attempts < 12 && (await get(dbRef(db, `${CODES}/${code}`))).exists());

  const meta = {
    hostUid: user.uid,
    status: 'lobby',
    roomCode: code,
    // The campaign's name and year: what the Load screen lists a game by.
    name: String(name || '').trim().slice(0, 40) || `The commonwealth of ${(user.displayName || 'the host').split(' ')[0]}`,
    year: 1,
    createdAt: new Date().toISOString(),
    lastActiveAt: Date.now(),
    // The table takes a slot but is flagged as one: the rules insist
    // `playerSlots` exists, and Firebase drops an empty array, so a room with
    // nobody in it yet would be refused outright. `playersIn` filters it back
    // out everywhere it matters, so the TV holds no role and is not a player.
    playerSlots: [asTable
      ? { uid: user.uid, displayName: 'The table', seatIndex: 0, table: true }
      : { uid: user.uid, displayName: user.displayName || 'Host', seatIndex: 0 }],
    // role -> uid. Written as an object with every key present so RTDB
    // never drops it; null means unclaimed.
    roles: ROLES.reduce((acc, r) => ({ ...acc, [r]: null }), {}),
  };

  await set(dbRef(db, `${GAMES}/${gameId}/meta`), meta);
  await set(dbRef(db, `${CODES}/${code}`), gameId);
  await set(dbRef(db, `${USER_GAMES}/${user.uid}/${gameId}`), { role: asTable ? 'table' : 'host', joinedAt: meta.createdAt });
  await set(dbRef(db, `${OPEN}/${gameId}`), openEntry(meta)).catch(() => {});

  return { gameId, code };
};

/**
 * Joins a room by code. A phone takes a seat (once); a table takes none and
 * may join a game at any stage - it is only ever looking.
 */
export const joinRoom = async ({ user, code, asTable = false }) => {
  if (!db) throw new Error('Online play needs Firebase configured (see .env.example).');
  if (!user) throw new Error('Sign in first.');

  const normalized = normalizeRoomCode(code);
  const codeSnap = await get(dbRef(db, `${CODES}/${normalized}`));
  if (!codeSnap.exists()) throw new Error(`No game found for code "${normalized}".`);
  const gameId = codeSnap.val();

  const metaSnap = await get(dbRef(db, `${GAMES}/${gameId}/meta`));
  if (!metaSnap.exists()) throw new Error('That game no longer exists.');
  const meta = metaSnap.val();

  if (asTable) return { gameId };

  const slots = meta.playerSlots || [];
  if (needsSlot(meta, user.uid)) {
    if (meta.status !== 'lobby') throw new Error('That game has already started.');
    if (playersIn(meta).length >= MAX_PLAYERS) throw new Error('That game is full.');
    slots.push({ uid: user.uid, displayName: user.displayName || 'Guest', seatIndex: slots.length });
    await update(dbRef(db, `${GAMES}/${gameId}/meta`), { playerSlots: slots, lastActiveAt: Date.now() });
    await set(dbRef(db, `${USER_GAMES}/${user.uid}/${gameId}`), { role: 'guest', joinedAt: new Date().toISOString() });
    await update(dbRef(db, `${OPEN}/${gameId}`), { uids: playersIn({ playerSlots: slots }).map((p) => p.uid) }).catch(() => {});
  }
  return { gameId };
};

/** Claim a role for yourself, or let one go (uid null). Lobby only. */
export const claimRole = async ({ user, gameId, meta, role, take }) => {
  if (!db) throw new Error('Online play needs Firebase configured.');
  if (meta.status !== 'lobby') throw new Error('The game has already started.');
  if (!ROLES.includes(role)) throw new Error('No such role.');
  const holder = meta.roles?.[role] || null;
  if (take && holder && holder !== user.uid) throw new Error('Somebody already holds that role.');
  if (!take && holder !== user.uid) return;
  await update(dbRef(db, `${GAMES}/${gameId}/meta/roles`), { [role]: take ? user.uid : null });
};

export const leaveLobby = async ({ user, gameId }) => {
  if (!db || !user || !gameId) return;
  const metaSnap = await get(dbRef(db, `${GAMES}/${gameId}/meta`));
  if (!metaSnap.exists()) return;
  const meta = metaSnap.val();
  if (meta.status !== 'lobby') return;
  // The table's slot stays: it is what keeps `playerSlots` from going empty,
  // which the database rules refuse, and it may share this uid.
  const slots = (meta.playerSlots || [])
    .filter((s) => s.table || s.uid !== user.uid)
    .map((s, i) => ({ ...s, seatIndex: i }));
  const roles = ROLES.reduce((acc, r) => ({ ...acc, [r]: meta.roles?.[r] === user.uid ? null : (meta.roles?.[r] || null) }), {});
  await update(dbRef(db, `${GAMES}/${gameId}/meta`), { playerSlots: slots, roles });
  await remove(dbRef(db, `${GAMES}/${gameId}/presence/${user.uid}`));
  await update(dbRef(db, `${OPEN}/${gameId}`), { uids: playersIn({ playerSlots: slots }).map((p) => p.uid) }).catch(() => {});
};

/** Host-only: bots for the roles nobody claims (a solo playtest). */
export const setRoomBots = async ({ gameId, on }) => {
  await update(dbRef(db, `${GAMES}/${gameId}/meta`), { bots: Boolean(on) });
};

/** Host-only: deals the game. Unclaimed roles go to the host. */
export const startRoom = async ({ user, gameId, meta }) => {
  if (!db) throw new Error('Online play needs Firebase configured.');
  if (meta.hostUid !== user?.uid) throw new Error('Only the host can start.');
  const seats = playersIn(meta);
  if (seats.length === 0) throw new Error('Nobody has joined yet - scan the code with a phone.');
  let players = seats.map((s) => ({ id: s.uid, name: s.displayName }));
  let roles = settledRoles(meta);
  // Bots for the roles nobody claimed (a solo playtest): the claimed ones
  // stay with their people; nothing falls back to the host.
  if (meta.bots) {
    const empty = ROLES.filter((r) => !meta.roles?.[r]);
    roles = ROLES.reduce((acc, r) => ({ ...acc, [r]: meta.roles?.[r] || botFor(r) }), {});
    players = [...players, ...empty.map((r) => ({ id: botFor(r), name: `Bot (${ROLE_INFO[r].label})`, bot: true }))];
  }
  const seed = Math.floor(Math.random() * 2 ** 31);
  const gameState = createGameState({ players, roles, seed });
  await set(dbRef(db, `${GAMES}/${gameId}/state`), encode(gameState));
  await update(dbRef(db, `${GAMES}/${gameId}/meta`), { status: 'in-progress', lastActiveAt: Date.now() });
  await closeOpen(db, gameId);
  return gameState;
};

/**
 * Drop the socket and dial again. An iPad's connection can die without
 * Firebase noticing: the screen stops updating (Seth's showed 5 knowledge
 * while the room had 9) and a move waits forever on a transaction that
 * never reaches the server - the "frozen" Scholars board of playtest
 * 6_yRq and the whole evening before it. A reload was the only cure; this
 * is the reload, without the reload.
 */
export const kickConnection = () => {
  if (!db) return;
  try { goOffline(db); goOnline(db); } catch { /* nothing to kick */ }
};
const STALL_MS = 5000;

export const commitAction = async ({ gameId, expectedSeq, apply, rebase = null }) => {
  if (!db) throw new Error('Online play needs Firebase configured.');
  // A move that has not landed in five seconds is a dead socket, not a slow one.
  const stall = setTimeout(kickConnection, STALL_MS);
  const result = await runTransaction(dbRef(db, `${GAMES}/${gameId}/state`), (raw) => {
    const current = decode(raw);
    if (!current) return raw;
    // Someone else moved first. Usually that sinks this move; `rebase` says
    // when it is safe to play it on top instead.
    if (seqOf(current) !== expectedSeq && !(rebase && rebase(current))) return undefined;
    const next = apply(current);
    if (!next || seqOf(next) === seqOf(current)) return undefined;
    return encode(next);
  }).finally(() => clearTimeout(stall));
  return { committed: result.committed, state: decode(result.snapshot.val()) };
};

export const sweepMyRooms = async ({ user, now = Date.now(), database = db }) => {
  if (!database || !user) return { deleted: 0, pruned: 0 };
  let deleted = 0;
  let pruned = 0;
  const mine = await get(dbRef(database, `${USER_GAMES}/${user.uid}`));
  if (!mine.exists()) return { deleted, pruned };
  for (const [gameId, entry] of Object.entries(mine.val() || {})) {
    try {
      // eslint-disable-next-line no-await-in-loop
      const metaSnap = await get(dbRef(database, `${GAMES}/${gameId}/meta`));
      if (!metaSnap.exists()) {
        // eslint-disable-next-line no-await-in-loop
        await remove(dbRef(database, `${USER_GAMES}/${user.uid}/${gameId}`));
        pruned += 1;
        continue;
      }
      if (entry?.role !== 'host' && metaSnap.val().hostUid !== user.uid) continue;
      // eslint-disable-next-line no-await-in-loop
      const presenceSnap = await get(dbRef(database, `${GAMES}/${gameId}/presence`));
      const meta = metaSnap.val();
      if (!isDeadRoom(meta, presenceSnap.val() || {}, now)) continue;
      // eslint-disable-next-line no-await-in-loop
      await remove(dbRef(database, `${GAMES}/${gameId}`));
      if (meta.roomCode) {
        // eslint-disable-next-line no-await-in-loop
        await remove(dbRef(database, `${CODES}/${meta.roomCode}`)).catch(() => {});
      }
      // eslint-disable-next-line no-await-in-loop
      await remove(dbRef(database, `${USER_GAMES}/${user.uid}/${gameId}`)).catch(() => {});
      // eslint-disable-next-line no-await-in-loop
      await closeOpen(database, gameId);
      deleted += 1;
    } catch {
      // best effort, always
    }
  }
  return { deleted, pruned };
};

// A stable id for this browser tab (survives a reload of the same tab, not a
// new one), so a screen's presence child is its own to remove.
const SCREEN_KEY = 'commonwealth:screen';
const screenId = () => {
  try {
    let id = sessionStorage.getItem(SCREEN_KEY);
    if (!id) {
      id = Math.random().toString(36).slice(2, 10);
      sessionStorage.setItem(SCREEN_KEY, id);
    }
    return id;
  } catch {
    return Math.random().toString(36).slice(2, 10);
  }
};

export const bindRoom = ({ gameId, user, onMeta, onState, onPresence }) => {
  if (!db) throw new Error('Online play needs Firebase configured.');
  const metaRef = dbRef(db, `${GAMES}/${gameId}/meta`);
  const stateRef = dbRef(db, `${GAMES}/${gameId}/state`);
  const presenceRef = dbRef(db, `${GAMES}/${gameId}/presence`);

  const offMeta = onValue(metaRef, (snap) => onMeta(snap.val()));
  const offState = onValue(stateRef, (snap) => onState(decode(snap.val())));
  const offPresence = onValue(presenceRef, (snap) => onPresence(snap.val() || {}));

  // One child per SCREEN under the uid, not one flag per uid: a player's
  // phone and their TV (the table, opened on the same Google account) are
  // both "here", and a TV tab closing must not grey out the phone. Readers
  // keep treating `presence[uid]` as truthy and counting its keys, and the
  // uid node vanishes on its own once the last screen's child is removed.
  let myPresenceRef = null;
  if (user) {
    myPresenceRef = dbRef(db, `${GAMES}/${gameId}/presence/${user.uid}/${screenId()}`);
    onDisconnect(myPresenceRef).remove();
    set(myPresenceRef, true);
    update(dbRef(db, `${GAMES}/${gameId}/meta`), { lastActiveAt: Date.now() }).catch(() => {});
  }

  // Playtest 7: Brian's phone sat on a finished day until he refreshed -
  // the listener had simply stopped hearing. Re-read the room whenever the
  // phone comes back to the foreground or Firebase says it reconnected, and
  // put this screen's presence back while we are at it.
  const resync = () => {
    get(stateRef).then((snap) => onState(decode(snap.val()))).catch(() => {});
    get(metaRef).then((snap) => onMeta(snap.val())).catch(() => {});
    if (myPresenceRef) { onDisconnect(myPresenceRef).remove(); set(myPresenceRef, true).catch(() => {}); }
  };
  // Back in the foreground: dial again first (a backgrounded iPad's socket
  // is often dead without knowing it), then re-read.
  const onVisible = () => { if (document.visibilityState === 'visible') { kickConnection(); resync(); } };
  document.addEventListener('visibilitychange', onVisible);
  window.addEventListener('online', resync);
  window.addEventListener('focus', resync);
  let wasConnected = true;
  const offConnected = onValue(dbRef(db, '.info/connected'), (snap) => {
    const now = snap.val() === true;
    if (now && !wasConnected) resync();
    wasConnected = now;
  });

  return () => {
    offMeta();
    offState();
    offPresence();
    offConnected();
    document.removeEventListener('visibilitychange', onVisible);
    window.removeEventListener('online', resync);
    window.removeEventListener('focus', resync);
    if (myPresenceRef) remove(myPresenceRef);
  };
};

/** The year a campaign is in, kept on the meta so the Load screen can say it without reading the whole state. */
export const noteYear = async ({ gameId, year }) => {
  if (!db || !gameId || !Number.isFinite(year)) return;
  await update(dbRef(db, `${GAMES}/${gameId}/meta`), { year, lastActiveAt: Date.now() }).catch(() => {});
};

/**
 * THE BOOK: every campaign this person is part of, newest first, with its
 * name, code, year and status. The Load screen (DESIGN.md "The Year").
 */
export const listCampaigns = async ({ user, database = db }) => (await readBook({ user, database })).campaigns;

/**
 * The book and the people in it, from one pass over this person's games:
 * `campaigns` as listCampaigns says, `friends` everybody they have shared a
 * room with (lobbies included).
 */
export const readBook = async ({ user, database = db }) => {
  if (!database || !user) return { campaigns: [], friends: [] };
  const mine = await get(dbRef(database, `${USER_GAMES}/${user.uid}`));
  if (!mine.exists()) return { campaigns: [], friends: [] };
  const out = [];
  const metas = [];
  for (const [gameId, entry] of Object.entries(mine.val() || {})) {
    // eslint-disable-next-line no-await-in-loop
    const metaSnap = await get(dbRef(database, `${GAMES}/${gameId}/meta`)).catch(() => null);
    if (!metaSnap?.exists()) continue;
    const meta = metaSnap.val();
    metas.push(meta);
    if (meta.status === 'lobby') continue;
    out.push({
      gameId, name: meta.name || meta.roomCode, roomCode: meta.roomCode, status: meta.status, year: meta.year || 1,
      lastActiveAt: lastActiveOf(meta), role: entry?.role || 'player', host: meta.hostUid === user.uid,
    });
  }
  return { campaigns: out.sort((a, b) => b.lastActiveAt - a.lastActiveAt), friends: friendsOf(metas, user.uid) };
};

/**
 * Watch the open rooms (a small node: a few words per lobby). Anyone signed
 * in may tidy an entry over a day old, so a lobby that never started does
 * not linger on everybody's landing.
 */
export const watchOpenRooms = ({ onRooms, database = db }) => {
  if (!database) return () => {};
  let swept = false;
  return onValue(dbRef(database, OPEN), (snap) => {
    const rooms = snap.val() || {};
    onRooms(rooms);
    if (swept) return;
    swept = true;
    Object.entries(rooms).filter(([, r]) => staleOpen(r)).forEach(([id]) => closeOpen(database, id));
  }, () => onRooms({}));
};

/**
 * Take a campaign out of the book. The host deletes it for everybody (the
 * game, its code, their own entry - the rules let only the host remove a
 * game); anyone else only drops it from their own list. Other players'
 * entries for a deleted game are pruned by listCampaigns / sweepMyRooms,
 * which skip or remove an entry whose game has gone.
 */
export const forgetCampaign = async ({
  user, gameId, roomCode, host, database = db,
}) => {
  if (!database || !user || !gameId) return;
  if (host) {
    await remove(dbRef(database, `${GAMES}/${gameId}`));
    if (roomCode) await remove(dbRef(database, `${CODES}/${roomCode}`)).catch(() => {});
    await closeOpen(database, gameId);
  }
  await remove(dbRef(database, `${USER_GAMES}/${user.uid}/${gameId}`));
};
